import { makeAutoObservable, runInAction } from 'mobx'
import User from 'src/models/User'
import apiClient from 'src/utils/apiClient'
import { UpdateEmailParams } from 'src/utils/apiClient/auth'
import { AuthorizationUserType } from 'src/utils/authorizationUserType'
import Cognito from 'src/utils/Cognito'

export default class AuthStore {
  user: User | null = null
  isLoading = true
  emailForForgotPassword = ''
  userAttributes: any = null

  get isLoggedIn() {
    return !!this.user
  }

  get userId() {
    return this.user?.id
  }

  get isTemporaryRegistration() {
    return this.userAttributes !== null
  }

  // Constructor ------------

  constructor() {
    makeAutoObservable(this)
    this.getUser()
  }

  // Actions ---------------

  getUser = async () => {
    const userIdStr = localStorage.getItem('userId')
    let userId = userIdStr ? Number(userIdStr) : void 0
    if (userId && isNaN(userId)) userId = void 0

    try {
      const userData = await apiClient.auth.getUser(userId)
      if (!userData) return

      runInAction(() => {
        this.user = new User(userData)
      })
    } catch (e) {
      console.log(e)
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  logIn = async (email: string, password: string, userCode?: string) => {
    const type = userCode ? AuthorizationUserType.Staff : AuthorizationUserType.User
    const userData = await apiClient.auth.getUserFromEmail({
      type,
      email,
      loginCode: email,
      password,
      userCode,
    })

    const res = await (async () => {
      if (type === AuthorizationUserType.Staff) {
        return await Cognito.logIn(`${type}_${email}`, password)
      }

      // 一般ユーザー
      let code = userData.code
      if (/^\d+$/.test(code)) code = code.slice(0, -1)

      return await Cognito.logIn(`${type}_${code}`, password)
    })()

    runInAction(() => {
      if (type === AuthorizationUserType.Staff) localStorage.setItem('userId', String(userData.id))
      if (res.isTemporary) this.userAttributes = res.data
      this.user = new User(userData)
    })
  }

  logOut = () => {
    Cognito.logOut()
    this.user = null
    this.userAttributes = null
  }

  sendResetPasswordEmail = async (email: string) => {
    this.emailForForgotPassword = email
    await apiClient.auth.sendResetPasswordEmail({ email })
  }

  confirmPassword = async (params: { email: string; code: string; password: string }) => {
    await apiClient.auth.confirmPassword(params)
  }

  changeTemporaryPassword = async (newPassword: string) => {
    await Cognito.changeTemporaryPassword(newPassword, this.userAttributes)
    this.userAttributes = null
  }

  changePassword = async ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) => {
    await Cognito.changePassword(currentPassword, newPassword)
  }

  updateEmail = async (params: UpdateEmailParams) => {
    await apiClient.auth.updateEmail(params)
  }
}
